import React, { useEffect, useState } from 'react';
import { Loader2, Truck, Plus, Save, Trash2, X, MapPin } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/utils/adminApi';
import { formatCOP } from '@/utils/format';

const emptyRate = {
  department: '',
  city: 'todas',
  base_rate: 15000,
  weight_threshold_kg: 10,
  additional_rate_per_kg: 2000,
  free_shipping_threshold: 200000,
  estimated_days: '3-5 días hábiles',
};

const RateForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || emptyRate);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="bg-white rounded-2xl border border-[#2A5C43] p-6" data-testid="shipping-rate-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Departamento" required>
          <input
            value={form.department}
            onChange={(e) => set('department', e.target.value)}
            placeholder="Ej. Cundinamarca"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-department"
          />
        </Field>
        <Field label="Ciudad (usa 'todas' para cubrir todo el departamento)">
          <input
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
            placeholder="Ej. Bogotá o todas"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-city"
          />
        </Field>
        <Field label="Tarifa base (COP)" required>
          <input
            type="number"
            value={form.base_rate}
            onChange={(e) => set('base_rate', parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-base"
          />
        </Field>
        <Field label="Umbral de peso (kg)">
          <input
            type="number"
            step="0.1"
            value={form.weight_threshold_kg}
            onChange={(e) => set('weight_threshold_kg', parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-weight-threshold"
          />
        </Field>
        <Field label="Costo adicional por kg extra (COP)">
          <input
            type="number"
            value={form.additional_rate_per_kg}
            onChange={(e) => set('additional_rate_per_kg', parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-additional"
          />
        </Field>
        <Field label="Envío gratis desde (COP)">
          <input
            type="number"
            value={form.free_shipping_threshold}
            onChange={(e) => set('free_shipping_threshold', parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-free-threshold"
          />
        </Field>
        <Field label="Días de entrega estimados" className="md:col-span-2">
          <input
            value={form.estimated_days || ''}
            onChange={(e) => set('estimated_days', e.target.value)}
            placeholder="Ej. 3-5 días hábiles"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="rate-days"
          />
        </Field>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={onCancel} variant="outline" className="rounded-full" data-testid="rate-cancel">
          <X className="w-4 h-4 mr-2" /> Cancelar
        </Button>
        <Button
          onClick={() => onSave(form)}
          disabled={saving || !form.department}
          className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6"
          data-testid="rate-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, required, className = '', children }) => (
  <label className={`block ${className}`}>
    <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">
      {label} {required && <span className="text-[#D47A4A]">*</span>}
    </span>
    {children}
  </label>
);

const AdminShipping = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listShippingRates();
      setRates(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, []);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateShippingRate(editing.id, form);
      } else {
        await adminAPI.createShippingRate(form);
      }
      setShowForm(false);
      setEditing(null);
      await fetchRates();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar la tarifa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta tarifa de envío?')) return;
    try {
      await adminAPI.deleteShippingRate(id);
      await fetchRates();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-shipping-title">Zonas de envío</h1>
            <p className="text-[#57534E] mt-2">Configura tarifas por departamento y ciudad</p>
          </div>
          {!showForm && (
            <Button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-5"
              data-testid="add-shipping-rate"
            >
              <Plus className="w-4 h-4 mr-2" /> Añadir tarifa
            </Button>
          )}
        </div>

        {showForm && (
          <div className="mb-8">
            <RateForm
              initial={editing}
              saving={saving}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
          </div>
        ) : rates.length === 0 && !showForm ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E7E5E4]" data-testid="shipping-empty">
            <Truck className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
            <p className="text-[#57534E] mb-4">Aún no hay tarifas configuradas</p>
            <Button onClick={() => setShowForm(true)} className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Crear la primera
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="shipping-rates-list">
            {rates.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid={`shipping-rate-${r.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-[#D47A4A]" />
                      <p className="font-display font-semibold text-[#1C1917] capitalize">{r.department}</p>
                    </div>
                    <p className="text-xs text-[#57534E] capitalize">
                      {r.city === 'todas' ? 'Todo el departamento' : `Ciudad: ${r.city}`}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditing(r); setShowForm(true); }}
                      className="p-2 hover:bg-[#F7F2E8] rounded-full text-[#2A5C43]"
                      data-testid={`edit-rate-${r.id}`}
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="p-2 hover:bg-red-50 rounded-full text-red-600"
                      data-testid={`delete-rate-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Tarifa base</p>
                    <p className="font-display font-bold text-[#1C1917]">{formatCOP(r.base_rate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Envío gratis</p>
                    <p className="font-medium text-[#2A5C43]">Desde {formatCOP(r.free_shipping_threshold)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Peso incluido</p>
                    <p className="text-[#1C1917]">Hasta {r.weight_threshold_kg} kg</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Extra por kg</p>
                    <p className="text-[#1C1917]">{formatCOP(r.additional_rate_per_kg)}</p>
                  </div>
                </div>

                {r.estimated_days && (
                  <p className="text-xs text-[#57534E] mt-3 pt-3 border-t border-[#F5F5F4]">
                    Entrega: <span className="font-medium">{r.estimated_days}</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminShipping;
